import React from "react";
import {
  Box,
  Text,
  Input,
  Flex,
  HStack,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { HeadingText } from "@root/components/common/text/HeadingText";
import { SmallText } from "@root/components/common/text/SmallText";
import { FILE_TYPE, ROUTE_NAMES } from "@root/constants";
import { useFileSizeValidation } from "@root/hooks/useFileSizeValidation";
import { useLocation } from "react-router-dom";
import UploadCircleIcon from "@root/assets/icons/bluecircleIcon";
import PermitUploadIcon from "@root/assets/icons/permitUploadIcon";
import { InputBox, UploadIconButton } from "../style";
import { InfoIcon } from "@root/assets/icons/infoicon";
import GreenBoxIcon from "@root/assets/icons/greenfileicon";
import { HelpPopover } from "@root/components/common/help-popover";
import { PermitReviewHelpContent } from "./PermitReviewHelpContent";

export const UploadFileArea: React.FC<UploadFileAreaProps> = ({
  uploadingFiles,
  t,
  fileInputRef,
  handleFiles,
  onDrop: _onDrop, // intentionally unused; validation handled locally
  onCancelUpload,
  review,
}) => {
  const { pathname } = useLocation();
  const isPermitReview =
    pathname.startsWith(ROUTE_NAMES.PERMIT_REVIEW) &&
    review === FILE_TYPE.EXTRACTION_REVIEW;
  const isLegalReview =
    pathname.startsWith(ROUTE_NAMES.PERMIT_REVIEW) &&
    review === FILE_TYPE.EXCEL_AUTO_COMPLETE;
  const isDrawingAnalysis = pathname === ROUTE_NAMES.DRAWING_ANALYSIS;
  const isMultiDoc = pathname.startsWith(ROUTE_NAMES.CHECKLIST);
  const isLegalChatbot = pathname.startsWith(ROUTE_NAMES.LEGAL_CHATBOT);
  const isUploading = (uploadingFiles?.length ?? 0) > 0;
  const showHelpIcon = pathname === ROUTE_NAMES.PERMIT_REVIEW;

  const maxFileSizeMB = isMultiDoc
    ? Number(process.env.REACT_APP_MATERIAL_FILE_LIMIT) || 500
    : Number(process.env.REACT_APP_GLOBAL_FILE_SIZE_LIMIT) || 300;

  const validateFiles = useFileSizeValidation(maxFileSizeMB);

  const processFilesWithSizeLimit = (files: FileList | null) => {
    if (!files) return;

    const validFileList = validateFiles(files);

    if (!validFileList) return;

    handleFiles(validFileList);
  };

  //different icons for different views
  function getUploadIcon() {
    return <PermitUploadIcon />;
  }

  // Extracted function to render uploading files UI
  const renderUploadingFiles = () => {
    return uploadingFiles?.map((f) => {
      const isUploaded = f?.progress === 100;
      const isUploading = f?.progress < 100;

      return (
        <Badge
          key={f?.file?.name}
          display="flex"
          alignItems="center"
          gap={2}
          px={4}
          py={2}
          borderRadius="full"
          border="1px solid"
          borderColor="#71B48D"
          bg="white"
          fontSize="14px"
          fontWeight="400"
          color="#000000"
          textTransform="none"
        >
          <Text>{f?.file?.name}</Text>
          {isUploading && (
            <Spinner size="sm" color="#71B48D" thickness="2px" speed="0.65s" />
          )}
          {isUploaded && (
            <Flex
              alignItems="center"
              justifyContent="center"
              bg="#000000"
              borderRadius="full"
              w="20px"
              h="20px"
            >
              <CheckIcon w={3} h={3} color="white" />
            </Flex>
          )}
        </Badge>
      );
    });
  };

  const getSupportedFileTypes = () => {
    if (isPermitReview) {
      return t("upload_document.allowed_file_types_pdf");
    }
    if (isLegalReview) {
      return t("upload_document.allowed_file_types_xlsx_xls");
    }
    if (isDrawingAnalysis) {
      return t("upload_document.allowed_file_types_pdf");
    }
    return t("upload_document.allowed_file_types_pdf_docx_xlsx");
  };

  const getAllowedFileTypes = () => {
    if (isPermitReview) {
      return ".pdf";
    }
    if (isLegalReview) {
      return ".xlsx,.xls";
    }
    if (isDrawingAnalysis) {
      return ".pdf";
    }
    return ".pdf,.docx,.xlsx,.xls";
  };

  const getFileUploadText = () => {
    if (isDrawingAnalysis) {
      return t("upload_document.drag_drop_here_drawing");
    }
    return t("upload_document.drag_drop_here");
  };

  return (
    <Box
      padding={"15px"}
      borderRadius={"8px"}
      border={"1px solid #CDD6DC99"}
      bg={"#fff"}
    >
      <Flex gap={"10px"} alignItems={"center"} mb={3}>
        <HeadingText
          color="#15161A"
          text={
            isDrawingAnalysis
              ? t("upload_document.permit_document_upload")
              : t("upload_document.document_upload")
          }
        />
        {showHelpIcon && (
          <HelpPopover>
            <PermitReviewHelpContent />
          </HelpPopover>
        )}
      </Flex>

      <Box
        shadow={"0px 4px 12px 0px #D0CDE142"}
        borderRadius={"18px"}
        padding={"5px"}
        position="relative"
        sx={{
          background: `
            url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg'%3e%3crect x='1' y='1' width='99%25' height='99%25' fill='none' rx='17' ry='17' stroke='%23349AE2' stroke-width='1.3' stroke-dasharray='8 10' stroke-linecap='round'/%3e%3c/svg%3e"),
            ${isUploading ? "#E8F5E9" : "#349AE20A"}
          `,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
        }}
      >
        <InputBox
          onClick={() => {
            if (isUploading) return;
            fileInputRef.current?.click();
          }}
          onDrop={(e: React.DragEvent) => {
            if (isUploading) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            processFilesWithSizeLimit(e.dataTransfer.files);
          }}
          onDragOver={(e: any) => e.preventDefault()}
          cursor={isUploading ? "default" : "pointer"}
          aria-disabled={isUploading}
          bg={"transparent"}
          border="none"
        >
          <Input
            type="file"
            accept={getAllowedFileTypes()}
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              if (isUploading) return;
              processFilesWithSizeLimit(e.target.files);
            }}
            disabled={isUploading}
          />
          {uploadingFiles?.length > 0 ? (
            <GreenBoxIcon />
          ) : (
            <UploadIconButton
              aria-label="upload"
              icon={getUploadIcon()}
              isRound
            />
          )}
          {uploadingFiles?.length > 0 ? null : (
            <HStack spacing={2}>
              <Text fontWeight="500" fontSize="lg" color="#2F2F34">
                {getFileUploadText()}
              </Text>
              <Text color="#349AE2" fontWeight="bold">
                {t("upload_document.chooseFiles")}
              </Text>
            </HStack>
          )}

          {uploadingFiles?.length > 0 ? (
            <Flex
              pt={6}
              w="100%"
              gap={3}
              flexWrap="wrap"
              justifyContent="center"
            >
              {renderUploadingFiles()}
            </Flex>
          ) : (
            <Flex
              fontSize="sm"
              p={2}
              borderRadius="20px"
              border={
                isPermitReview ? "0.5px solid #349AE2" : "0.5px solid #40599B"
              }
              mt={4}
              alignItems="center"
              gap={2} // spacing between icon and text
              bg={"white"}
            >
              <InfoIcon color={isPermitReview ? "#349AE2" : "#40599B"} />
              <Box
                as="span"
                color={"#231F20"}
                dangerouslySetInnerHTML={{
                  __html: getSupportedFileTypes(),
                }}
              />
            </Flex>
          )}
        </InputBox>
      </Box>
    </Box>
  );
};
